import { useCallback, useEffect, useMemo, useState } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Alert from "react-bootstrap/Alert";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import "./App.css";
import AppNavbar from "./AppNavbar.js";
import SectionCard from "./SectionCard.js";
import { communityFetch } from "./communityApi.js";
import {
  buildLoginUrl,
  buildLogoutUrl,
  buildSignupUrl,
  getTokenStorageKey,
  isTokenExpired,
  readTokenFromUrlHash,
} from "./auth.js";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const COGNITO_DOMAIN = process.env.REACT_APP_COGNITO_DOMAIN || "";
const COGNITO_CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID || "";
const COGNITO_REDIRECT_URI = `${window.location.origin}/communities`;
const COGNITO_LOGOUT_URI = `${window.location.origin}/communities`;

function CommunitiesPage() {
  const [idToken, setIdToken] = useState("");
  const [me, setMe] = useState(null);
  const [balance, setBalance] = useState({ cash_available: 0, cash_reserved: 0 });
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const tokenFromHash = readTokenFromUrlHash();
    const storedToken = localStorage.getItem(getTokenStorageKey()) || "";
    const token = tokenFromHash || storedToken;

    if (!token || isTokenExpired(token)) {
      localStorage.removeItem(getTokenStorageKey());
      setIdToken("");
      return;
    }

    localStorage.setItem(getTokenStorageKey(), token);
    setIdToken(token);
    if (tokenFromHash) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const loginUrl = useMemo(
    () => buildLoginUrl(COGNITO_DOMAIN, COGNITO_CLIENT_ID, COGNITO_REDIRECT_URI),
    []
  );
  const signupUrl = useMemo(
    () => buildSignupUrl(COGNITO_DOMAIN, COGNITO_CLIENT_ID, COGNITO_REDIRECT_URI),
    []
  );
  const logoutUrl = useMemo(
    () => buildLogoutUrl(COGNITO_DOMAIN, COGNITO_CLIENT_ID, COGNITO_LOGOUT_URI),
    []
  );

  const authFetch = useCallback(async (path) => {
    if (!API_BASE_URL) {
      throw new Error("Missing REACT_APP_API_BASE_URL");
    }
    if (!idToken) {
      throw new Error("Not signed in");
    }
    const response = await fetch(`${API_BASE_URL}${path}`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
    });
    const body = await response.json();
    if (!response.ok) {
      throw new Error(body.error || body.message || "Request failed");
    }
    return body;
  }, [idToken]);

  const loadPage = useCallback(async () => {
    if (!idToken) {
      setMe(null);
      setBalance({ cash_available: 0, cash_reserved: 0 });
      setCommunities([]);
      setError("");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const [meRes, balanceRes, communitiesRes] = await Promise.all([
        authFetch("/me"),
        authFetch("/me/balance"),
        communityFetch("/communities", idToken),
      ]);
      setMe(meRes);
      setBalance(balanceRes);
      setCommunities(Array.isArray(communitiesRes.items) ? communitiesRes.items : []);
    } catch (err) {
      setError(err.message || "Failed to load communities");
    } finally {
      setLoading(false);
    }
  }, [authFetch, idToken]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const handleLogin = () => {
    if (!loginUrl) {
      setError("Missing Cognito configuration in React env vars");
      return;
    }
    window.location.assign(loginUrl);
  };

  const handleSignup = () => {
    if (!signupUrl) {
      setError("Missing Cognito configuration in React env vars");
      return;
    }
    window.location.assign(signupUrl);
  };

  const handleLogout = () => {
    localStorage.removeItem(getTokenStorageKey());
    setIdToken("");
    setMe(null);
    setBalance({ cash_available: 0, cash_reserved: 0 });
    if (logoutUrl) {
      window.location.assign(logoutUrl);
    }
  };

  return (
    <div className="App">
      <AppNavbar
        links={[
          { href: "/", label: "Home" },
          { href: "/open-orders", label: "Open Orders" },
          { href: "/communities", label: "Communities" },
          { href: "/leaderboard", label: "Leaderboard" },
          { href: "/market-resolution", label: "Resolve Market" },
          { href: "/markets/new", label: "Create Market" },
        ]}
        isSignedIn={Boolean(idToken)}
        me={me}
        balance={balance}
        onLogin={handleLogin}
        onLogout={handleLogout}
        onSignup={handleSignup}
      />
      <Container className="py-4">
        {error && (
          <Row className="mb-3">
            <Col>
              <Alert variant="danger" className="mb-0">{error}</Alert>
            </Col>
          </Row>
        )}
        {!idToken ? (
          <Row>
            <Col>
              <Alert variant="info" className="mb-0">
                Sign in with Cognito to browse communities.
              </Alert>
            </Col>
          </Row>
        ) : (
          <Row className="g-4">
            <Col lg={12}>
              <SectionCard title="Communities">
                <div className="community-summary-row">
                  <span className="community-summary-copy">
                    Browse public and private communities. Private communities are visible, but their markets stay hidden until your membership is approved.
                  </span>
                  <Button href="/communities/new" variant="outline-light">
                    Create Community
                  </Button>
                </div>
                {loading ? (
                  <Alert variant="secondary" className="mt-3 mb-0">Loading communities...</Alert>
                ) : communities.length === 0 ? (
                  <Alert variant="secondary" className="mt-3 mb-0">No communities found.</Alert>
                ) : (
                  <div className="community-grid">
                    {communities.map((community) => (
                      <article key={community.community_id} className="community-card">
                        <div className="community-card-header">
                          <h3>{community.name}</h3>
                          <Badge bg={community.visibility === "PRIVATE" ? "warning" : "success"} text="dark">
                            {community.visibility}
                          </Badge>
                        </div>
                        {community.description ? (
                          <p className="community-card-description">{community.description}</p>
                        ) : (
                          <p className="community-card-description community-card-description-muted">
                            No description yet.
                          </p>
                        )}
                        <div className="community-card-meta">
                          <span>Created by {community.created_by_username || community.created_by_user_id}</span>
                          {community.membership_status && (
                            <span>Your status: {community.membership_status.toLowerCase()}</span>
                          )}
                        </div>
                        <div className="community-card-actions">
                          <Button href={`/communities/${community.community_id}`} variant="light">
                            View Community
                          </Button>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </SectionCard>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
}

export default CommunitiesPage;
